import React, { useState, useEffect, useRef, useCallback } from "react";
import * as topojson from "topojson-client";
import apiClient from "../../../../../api/apiClient";
import { geocodeLead, STATE_CENTROIDS, STATE_ABBR_MAP } from "../shared/usCityCoords";
import "./LeadGlobeModal.css";

// Fetch all leads across pages for the globe (no pagination limit)
async function fetchAllGlobeLeads() {
    const allLeads = [];
    let page = 1;
    let pages = 1;
    do {
        const res = await apiClient.get(
            `/admin/crm/leads?page=${page}&limit=100&sort=createdAt&order=asc`
        );
        const data = res.data;
        allLeads.push(...(data.leads || []));
        pages = data.pages || 1;
        page++;
    } while (page <= pages);
    return allLeads;
}

// Fetch and parse both TopoJSON geo files
async function fetchGeoData() {
    const [worldTopo, usTopo] = await Promise.all([
        fetch(`${process.env.PUBLIC_URL}/geo/world-110m.json`).then(r => r.json()),
        fetch(`${process.env.PUBLIC_URL}/geo/us-states-10m.json`).then(r => r.json()),
    ]);
    return { worldTopo, usTopo };
}

// Group geocoded leads by location, return pointsData array and unmapped count
function buildPointsData(leads) {
    const locationMap = {};
    let unmappedCount = 0;

    for (const lead of leads) {
        const coords = geocodeLead(lead.city, lead.state);
        if (!coords) {
            unmappedCount++;
            continue;
        }
        const key = `${coords.lat.toFixed(4)},${coords.lng.toFixed(4)}`;
        if (!locationMap[key]) {
            locationMap[key] = {
                lat: coords.lat,
                lng: coords.lng,
                city: lead.city || "",
                state: lead.state || "",
                leads: [],
            };
        }
        locationMap[key].leads.push({
            id: lead.id,
            firstName: lead.firstName,
            lastName: lead.lastName,
        });
    }

    const pointsData = Object.values(locationMap).map(p => ({
        ...p,
        count: p.leads.length,
    }));

    return { pointsData, unmappedCount };
}

// Normalize a state string (full name or abbr) to a lowercase 2-letter abbreviation
function normalizeStateAbbr(state) {
    if (!state) return null;
    const s = state.trim().toLowerCase();
    if (s.length === 2) return s;
    return STATE_ABBR_MAP[s] || null;
}

// Pre-build state label data from centroids
const STATE_LABEL_DATA = Object.entries(STATE_CENTROIDS).map(([abbr, [lat, lng]]) => ({
    _type: "stateLabel",
    lat,
    lng,
    abbr: abbr.toUpperCase(),
    abbrLower: abbr,
}));

// Polygon cap color function — called with current selected state abbr
function makePolygonCapColor(selectedAbbr) {
    return f => {
        if (f._isUSState) {
            const fName = (f.properties?.name || "").toLowerCase();
            const fAbbr = STATE_ABBR_MAP[fName];
            if (fAbbr && fAbbr === selectedAbbr) return "rgba(52, 235, 229, 0.50)";
            return "rgba(42, 177, 172, 0.21)";
        }
        return "#2353502f";
    };
}

export default function LeadGlobeModal({ onClose, onSelectLead }) {
    const globeContainerRef = useRef(null);
    const globeInstanceRef  = useRef(null);
    const pointsDataRef     = useRef([]);
    const selectedStateRef  = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pointsData, setPointsData] = useState([]);
    const [unmappedCount, setUnmappedCount] = useState(0);
    const [tooltipInfo, setTooltipInfo] = useState(null); // { x, y, point }
    const [clickedLocation, setClickedLocation] = useState(null);
    const [clickedState, setClickedState] = useState(null); // { name, abbr, cities, totalLeads }

    const tooltipInfoRef = useRef(null);

    const handlePointHover = useCallback((point, coords) => {
        if (!point) {
            setTooltipInfo(null);
            return;
        }
        const rect = globeContainerRef.current?.getBoundingClientRect();
        if (rect && coords) {
            setTooltipInfo({ x: coords.x, y: coords.y, point });
        }
    }, []);

    const handlePointClick = useCallback((point) => {
        if (!point) return;
        // Clear state selection when clicking a lead dot
        if (selectedStateRef.current) {
            selectedStateRef.current = null;
            setClickedState(null);
            if (globeInstanceRef.current) {
                globeInstanceRef.current.polygonCapColor(makePolygonCapColor(null));
            }
        }
        setClickedLocation(point);
    }, []);

    const handlePolygonClick = useCallback((polygon) => {
        if (!polygon._isUSState) return;
        const stateName = polygon.properties?.name || "";
        const stateAbbrLower = STATE_ABBR_MAP[stateName.toLowerCase()];
        if (!stateAbbrLower) return;

        // Filter pointsData for leads in this state
        const statePoints = pointsDataRef.current.filter(p => {
            const norm = normalizeStateAbbr(p.state);
            return norm === stateAbbrLower;
        });

        // Update selection highlight
        selectedStateRef.current = stateAbbrLower;
        if (globeInstanceRef.current) {
            globeInstanceRef.current.polygonCapColor(makePolygonCapColor(stateAbbrLower));
        }

        setClickedLocation(null); // close city panel
        setClickedState({
            name: stateName,
            abbr: stateAbbrLower.toUpperCase(),
            cities: statePoints,
            totalLeads: statePoints.reduce((sum, p) => sum + p.count, 0),
        });
    }, []);

    const clearStateSelection = useCallback(() => {
        selectedStateRef.current = null;
        setClickedState(null);
        if (globeInstanceRef.current) {
            globeInstanceRef.current.polygonCapColor(makePolygonCapColor(null));
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        // Update state-label opacity based on camera altitude — only runs on camera change, not every frame
        const updateLabelOpacity = () => {
            if (!globeInstanceRef.current) return;
            const alt = globeInstanceRef.current.pointOfView?.()?.altitude ?? 2;
            const opacity = alt < 1.3 ? Math.max(0, Math.min(1, (1.3 - alt) / (1.3 - 0.7))) : 0;
            const opStr = String(opacity.toFixed(3));
            globeContainerRef.current?.querySelectorAll(".globe-state-label").forEach(el => {
                el.style.opacity = opStr;
            });
        };

        async function init() {
            try {
                const [leadsResult, geoResult] = await Promise.all([
                    fetchAllGlobeLeads(),
                    fetchGeoData(),
                ]);

                if (cancelled) return;

                const { pointsData: pts, unmappedCount: unmapped } = buildPointsData(leadsResult);
                setPointsData(pts);
                setUnmappedCount(unmapped);
                pointsDataRef.current = pts;

                const { worldTopo, usTopo } = geoResult;

                // Convert TopoJSON → GeoJSON features
                const worldCountries = topojson.feature(worldTopo, worldTopo.objects.countries).features;
                const usStates = topojson.feature(usTopo, usTopo.objects.states).features;

                // Mark US states for custom styling and click handling
                usStates.forEach(f => { f._isUSState = true; });

                // Filter out the USA country polygon to avoid double-layering
                const nonUSCountries = worldCountries.filter(f => String(f.id) !== "840");
                const allPolygons = [...nonUSCountries, ...usStates];

                if (cancelled || !globeContainerRef.current) return;

                // Dynamically import globe.gl + THREE together
                const [{ default: Globe }, THREE] = await Promise.all([
                    import("globe.gl"),
                    import("three"),
                ]);

                if (cancelled || !globeContainerRef.current) return;

                const containerW = globeContainerRef.current.offsetWidth || 800;

                // Combined HTML data: lead dots + state labels
                const leadDotData = pts.map(p => ({ ...p, _type: "leadDot" }));
                const allHtmlData = [...leadDotData, ...STATE_LABEL_DATA];

                const globe = Globe()(globeContainerRef.current);
                globeInstanceRef.current = globe;

                globe
                    .width(containerW)
                    .height(560)
                    .backgroundColor("#0c161a")
                    .globeImageUrl(null)
                    .showAtmosphere(true)
                    .atmosphereColor("#218b89")
                    .atmosphereAltitude(0.07)

                    // Polygon layer: all countries + US states
                    .polygonsData(allPolygons)
                    .polygonAltitude(f => f._isUSState ? 0.006 : 0.002)
                    .polygonCapColor(makePolygonCapColor(null))
                    .polygonSideColor(f => {
                        if (f._isUSState) return "rgba(52, 235, 229, 0.35)";
                        return "rgba(52, 235, 229, 0.15)";
                    })
                    .polygonStrokeColor(f => {
                        if (f._isUSState) return "#34ebe5";
                        return "rgba(52, 235, 229, 0.55)";
                    })
                    .onPolygonClick(handlePolygonClick)

                    // Lead dots
                    .pointsData(pts)
                    .pointColor(() => "#91e0e5")
                    .pointAltitude(0.018)
                    .pointRadius(d => Math.max(0.25, 0.22 + Math.sqrt(d.count) * 0.16))
                    .pointResolution(6)

                    // HTML layer: lead count badges + state abbreviation labels
                    .htmlElementsData(allHtmlData)
                    .htmlElement(d => {
                        const el = document.createElement("div");
                        if (d._type === "stateLabel") {
                            el.className = "globe-state-label";
                            el.dataset.stateAbbr = d.abbrLower;
                            el.textContent = d.abbr;
                            el.style.opacity = "0"; // starts hidden, fades in on zoom
                        } else {
                            el.className = "globe-lead-dot";
                            el.textContent = d.count;
                        }
                        return el;
                    })
                    .htmlAltitude(d => d._type === "stateLabel" ? 0.007 : 0.028)

                    // Events
                    .onPointHover((point, ev) => {
                        handlePointHover(point, ev);
                    })
                    .onPointClick((point) => {
                        handlePointClick(point);
                    });

                // Fresnel shader — dark centre, bright blue rim at sphere edges
                globe.globeMaterial(new THREE.ShaderMaterial({
                    uniforms: {
                        fresnelColor:    { value: new THREE.Color("#34ebe5") },
                        baseColor:       { value: new THREE.Color("#0c161a") },
                        fresnelPower:    { value: 5.5 },
                        fresnelStrength: { value: 0.15 },
                    },
                    vertexShader: `
                        varying vec3 vNormal;
                        varying vec3 vWorldPosition;
                        void main() {
                            vNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
                            vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
                            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                        }
                    `,
                    fragmentShader: `
                        #include <common>
                        uniform vec3  fresnelColor;
                        uniform vec3  baseColor;
                        uniform float fresnelPower;
                        uniform float fresnelStrength;
                        varying vec3 vNormal;
                        varying vec3 vWorldPosition;
                        void main() {
                            vec3  viewDir = normalize(cameraPosition - vWorldPosition);
                            float fresnel = pow(1.0 - max(dot(vNormal, viewDir), 0.0), fresnelPower);
                            vec3  color   = mix(baseColor, fresnelColor, fresnel * fresnelStrength);
                            gl_FragColor  = vec4(color, 1.0);
                            #include <colorspace_fragment>
                        }
                    `,
                    side: THREE.FrontSide,
                }));

                // Focus on the continental USA
                globe.pointOfView({ lat: 39.5, lng: -98.35, altitude: 1.8 }, 1400);

                // ── Bloom post-processing via globe.gl's built-in composer ──
                // globe.gl already creates an internal EffectComposer with a RenderPass.
                // We just append UnrealBloomPass to it — no renderer.render monkey-patching needed.
                const { UnrealBloomPass } = await import("three/examples/jsm/postprocessing/UnrealBloomPass.js");

                if (cancelled) return;

                const renderer = globe.renderer();
                const scene    = globe.scene();

                scene.background = new THREE.Color("#0c161a");
                renderer.setClearColor(new THREE.Color("#0c161a"), 1);

                globe.postProcessingComposer().addPass(
                    new UnrealBloomPass(new THREE.Vector2(containerW, 560), 0.9, 0.4, 0.65)
                );

                // ── State label zoom-based fade ────────────────────────────
                // Labels fade in when altitude < 1.3, fully visible at < 0.7.
                // Uses OrbitControls 'change' event — fires only when camera moves, not every frame.
                globe.controls().addEventListener("change", updateLabelOpacity);

                setLoading(false);
            } catch (err) {
                if (!cancelled) {
                    console.error("LeadGlobeModal init error:", err);
                    setError("Failed to load globe. Please try again.");
                    setLoading(false);
                }
            }
        }

        init();

        return () => {
            cancelled = true;

            try {
                const globe = globeInstanceRef.current;
                if (globe) {
                    try { globe.controls().removeEventListener("change", updateLabelOpacity); } catch (_) {}
                    // _destructor() stops animation, disposes renderer, composer, and scene
                    globe._destructor();
                    const renderer = globe.renderer?.();
                    renderer?.getContext?.()?.getExtension?.("WEBGL_lose_context")?.loseContext();
                }
            } catch (_) {}

            globeInstanceRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        tooltipInfoRef.current = tooltipInfo;
    }, [tooltipInfo]);

    const handleNavigateToLead = (leadId) => {
        onClose();
        if (onSelectLead) onSelectLead(leadId);
    };

    return (
        <div
            className="crm-modal-overlay"
            onClick={e => e.target === e.currentTarget && onClose()}
        >
            <div className="crm-modal crm-modal--globe">
                <div className="crm-modal__header">
                    <h2 className="crm-modal__title">Lead Map</h2>
                    <button className="crm-modal__close" onClick={onClose}>×</button>
                </div>

                <div className="crm-modal__body crm-modal__body--globe">
                    {loading && (
                        <div className="globe-loading">
                            <div className="globe-loading__spinner" />
                            <span>Loading globe and lead data…</span>
                        </div>
                    )}
                    {error && (
                        <div className="globe-loading">
                            <span style={{ color: "var(--color-error, #ef4444)" }}>{error}</span>
                        </div>
                    )}

                    <div ref={globeContainerRef} className="globe-container" />

                    {/* Hover tooltip */}
                    {tooltipInfo && tooltipInfo.point && (
                        <div
                            className="globe-tooltip"
                            style={{
                                left: tooltipInfo.x + 14,
                                top: tooltipInfo.y - 10,
                            }}
                        >
                            <div className="globe-tooltip__location">
                                {tooltipInfo.point.city
                                    ? `${tooltipInfo.point.city}, ${tooltipInfo.point.state}`
                                    : tooltipInfo.point.state}
                            </div>
                            <div className="globe-tooltip__names">
                                {tooltipInfo.point.leads.slice(0, 5).map(l => (
                                    <div key={l.id}>{l.firstName} {l.lastName}</div>
                                ))}
                                {tooltipInfo.point.leads.length > 5 && (
                                    <div>+{tooltipInfo.point.leads.length - 5} more</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Click location panel (city-level) */}
                    {clickedLocation && (
                        <div className="globe-location-panel">
                            <div className="globe-location-panel__header">
                                <div className="globe-location-panel__title">
                                    {clickedLocation.city
                                        ? `${clickedLocation.city}, ${clickedLocation.state}`
                                        : clickedLocation.state}
                                </div>
                                <button
                                    className="globe-location-panel__close"
                                    onClick={() => setClickedLocation(null)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="globe-location-panel__count">
                                {clickedLocation.count} lead{clickedLocation.count !== 1 ? "s" : ""}
                            </div>
                            {clickedLocation.leads.map(l => (
                                <button
                                    key={l.id}
                                    className="globe-lead-link"
                                    onClick={() => handleNavigateToLead(l.id)}
                                    title={`${l.firstName} ${l.lastName}`}
                                >
                                    {l.firstName} {l.lastName}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* State click panel */}
                    {clickedState && (
                        <div className="globe-state-panel">
                            <div className="globe-state-panel__header">
                                <div className="globe-state-panel__title">
                                    <span className="globe-state-panel__abbr">{clickedState.abbr}</span>
                                    {clickedState.name}
                                </div>
                                <button
                                    className="globe-location-panel__close"
                                    onClick={clearStateSelection}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="globe-location-panel__count">
                                {clickedState.totalLeads} lead{clickedState.totalLeads !== 1 ? "s" : ""} across {clickedState.cities.length} {clickedState.cities.length !== 1 ? "cities" : "city"}
                            </div>

                            {clickedState.cities.length === 0 ? (
                                <div className="globe-state-panel__empty">No leads in this state</div>
                            ) : (
                                clickedState.cities.map((cityPoint, i) => (
                                    <div key={i} className="globe-state-panel__city-group">
                                        <div className="globe-state-panel__city-name">
                                            {cityPoint.city || "Unknown City"}
                                            <span className="globe-state-panel__city-count">{cityPoint.count}</span>
                                        </div>
                                        {cityPoint.leads.map(l => (
                                            <button
                                                key={l.id}
                                                className="globe-lead-link"
                                                onClick={() => handleNavigateToLead(l.id)}
                                                title={`${l.firstName} ${l.lastName}`}
                                            >
                                                {l.firstName} {l.lastName}
                                            </button>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <div className="globe-footer">
                    {!loading && !error && (
                        <>
                            <span>
                                <span className="globe-footer__dot" />
                                {pointsData.length} location{pointsData.length !== 1 ? "s" : ""} mapped
                            </span>
                            {unmappedCount > 0 && (
                                <span>
                                    {unmappedCount} lead{unmappedCount !== 1 ? "s" : ""} not on map
                                </span>
                            )}
                            <span className="globe-footer__hint">Zoom in to see state labels · Click a state for leads</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
